import { useState } from 'react';
import {
  List,
  Input,
  Space,
  Card,
  Spin,
  Tag,
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useCompany } from '../hooks/company';
import { userController, ListUserItemDTO, UserStateEnum } from '../api';
import DepartmentSelect from './DepartmentSelect';
import { findChildrenDepartment } from '../utils';
import { AvatarImg } from './OSSImage';

const { Search } = Input;

interface AuditUserListProps {
  selectedUserId?: number | string;
  onUserSelect: (user: ListUserItemDTO) => void;
}

export default function AuditUserList({ selectedUserId, onUserSelect }: AuditUserListProps) {
  const { id: companyId, departments, currentUser, roles } = useCompany();
  const [searchText, setSearchText] = useState('');
  const [departmentId, setDepartmentId] = useState<number | undefined>(undefined);

  // 获取用户列表
  const { data: usersData, loading: loadingUsers, run: loadUsers } = useRequest(
    async () => {
      if (!companyId) return null;

      // 处理部门ID，获取所有子部门
      const rootDepartmentId = departmentId || currentUser.departmentId;
      const deps = rootDepartmentId
        ? findChildrenDepartment(departments, rootDepartmentId).map((item) => item.id)
        : undefined;
      
      if (rootDepartmentId && deps) {
        deps.push(rootDepartmentId);
      }

      return await userController.listUser(companyId, 1, 1000, {
        companyId,
        userStates: UserStateEnum.ACTIVE,
        departmentIds: deps,
      });
    },
    {
      refreshDeps: [companyId, departmentId, departments, currentUser],
    },
  );

  const users = usersData?.list || [];
  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.mobile?.includes(searchText),
  );

  // 区域选择变化
  const handleDepartmentChange = (value: number | undefined) => {
    setDepartmentId(value);
  };

  return (
    <Card
      title={
        <Space>
          <UserOutlined />
          <span>人员列表</span>
        </Space>
      }
      className="audit-card"
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <div style={{ padding: '12px', flexShrink: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Search
            placeholder="搜索人员姓名/手机号"
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <DepartmentSelect
            style={{ width: '100%' }}
            placeholder="选择区域"
            value={departmentId}
            onChange={handleDepartmentChange}
            allowClear
          />
        </Space>
      </div>
      <div className="audit-list-container" style={{ flex: 1, overflow: 'hidden' }}>
        <Spin spinning={loadingUsers}>
          <List
            dataSource={filteredUsers}
            renderItem={(user) => {
              const role = roles?.find((r) => r.id === user.roleId);
              return (
                <List.Item
                  className={`audit-list-item ${
                    selectedUserId === user.id ? 'active' : ''
                  }`}
                  onClick={() => onUserSelect(user)}
                >
                  <List.Item.Meta
                    avatar={<AvatarImg userId={user.id} />}
                    title={
                      <Space>
                        <span>{user.name}</span>
                        {role && <Tag>{role.name}</Tag>}
                      </Space>
                    }
                    description={user.mobile}
                  />
                </List.Item>
              );
            }}
          />
        </Spin>
      </div>
    </Card>
  );
}

